import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { Box } from 'reflexbox';

import { useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	GraphQLErrorMessage,
	Heading3,
	ITimeMachine,
	Paragraph,
	Subheading,
} from '@treams/treams-ui';

import AnalyticsMetricDetailsExport from './AnalyticsMetricDetailsExport';
import AnalyticsMetricDetailsTable from './AnalyticsMetricDetailsTable';
import AnalyticsMetricDetailsTabs from './AnalyticsMetricDetailsTabs';

export default function AnalyticsMetricDetails(props: {
	ids: string[];
	type: graphql.AnalyticsType;
	filters: graphql.AnalyticsFiltersV2;
	timeMachine: ITimeMachine;
}) {
	const { t } = useTranslation();
	const match = useRouteMatch<{ id: string }>('/analytics/metric/:groupID/:metricID/:id')?.params
		.id;
	const { canViewAndEditAllSettings } = useViewerRoles();

	const id = props.ids.find((id) => _.kebabCase(id) === match) ?? props.ids[0];
	const { data, error } = graphql.useAnalyticsMetricDetailsQuery({
		variables: {
			id: id,
			filters: props.filters,
			type: props.type,
		},
	});

	return (
		<>
			<Heading3 mt={4} mb={2}>
				{t('analytics.metric.detailsTitle')}
			</Heading3>
			<Box mb={3}>
				<AnalyticsMetricDetailsTabs
					ids={props.ids}
					type={props.type}
					filters={props.filters}
					timeMachine={props.timeMachine}
					{...(canViewAndEditAllSettings
						? {
								downloadButton: data ? (
									<AnalyticsMetricDetailsExport
										id={id}
										filters={props.filters}
										type={props.type}
										timeMachine={props.timeMachine}
									/>
								) : (
									<Button.Skeleton small opacity={error ? '0' : '1'} />
								),
							}
						: {})}
				/>
			</Box>

			{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

			{t(`analytics.metric.details.description.${id}`) !==
				`analytics.metric.details.description.${id}` && (
				<Paragraph mb={3}>{t(`analytics.metric.details.description.${id}`)}</Paragraph>
			)}

			{data?.analytics.detailTableList.map((table, index) => (
				<div key={`metric-details-table-${table.id}`}>
					{table.title && (
						<Subheading mt={index > 0 ? 4 : 0} mb={1}>
							{table.title}
						</Subheading>
					)}
					<AnalyticsMetricDetailsTable
						tableID={table.id}
						id={id}
						filters={props.filters}
						type={props.type}
						timeMachine={props.timeMachine}
					/>
				</div>
			))}
		</>
	);
}
